import { EntityProfileType } from "../../../types/entity";
import {
    AccountClass,
    AccountType,
    toBalanceSheetAccountClass,
} from "./accounts";

export const accountTypeToAccountClass: Record<AccountType, AccountClass> = {
    [AccountType.cash]: AccountClass.assets,
    [AccountType.accountsReceivable]: AccountClass.assets,
    [AccountType.inventory]: AccountClass.assets,
    [AccountType.otherCurrentAssets]: AccountClass.assets,
    [AccountType.investments]: AccountClass.assets,
    [AccountType.fixedAssets]: AccountClass.assets,
    [AccountType.intangibleAssets]: AccountClass.assets,
    [AccountType.otherAssets]: AccountClass.assets,
    [AccountType.accountsPayable]: AccountClass.liabilities,
    [AccountType.creditCards]: AccountClass.liabilities,
    [AccountType.payrollLiabilities]: AccountClass.liabilities,
    [AccountType.shortTermLoans]: AccountClass.liabilities,
    [AccountType.otherCurrentLiabilities]: AccountClass.liabilities,
    [AccountType.longTermLoans]: AccountClass.liabilities,
    [AccountType.otherLiabilities]: AccountClass.liabilities,
    [AccountType.equity]: AccountClass.equity,
    [AccountType.income]: AccountClass.income,
    [AccountType.cogs]: AccountClass.expenses,
    [AccountType.operatingExpenses]: AccountClass.expenses,
    [AccountType.otherIncome]: AccountClass.income,
    [AccountType.otherExpenses]: AccountClass.expenses,
};

export const accountClassesIncreasingByDebit = [
    AccountClass.assets,
    AccountClass.expenses,
];

export const profitAndLossAccountClasses: readonly AccountClass[] = [
    AccountClass.income,
    AccountClass.expenses,
];

// Canonical Chart-of-Accounts class display order.
export const orderedAccountClasses: AccountClass[] = [
    AccountClass.assets,
    AccountClass.liabilities,
    AccountClass.equity,
    AccountClass.income,
    AccountClass.expenses,
];

export const accountClassesWithTypes = Object.keys(
    accountTypeToAccountClass,
).reduce(
    (acc, accountType) => {
        const accountClass =
            accountTypeToAccountClass[accountType as AccountType];
        if (!acc[accountClass]) {
            acc[accountClass] = [];
        }
        acc[accountClass].push(accountType as AccountType);
        return acc;
    },
    {} as Record<AccountClass, AccountType[]>,
);

export const isAccountType = (value: string): value is AccountType =>
    Object.values(AccountType).some((member) => member === value);

export const getAccountClassForType = (
    accountType: AccountType,
): AccountClass => {
    if (isAccountType(accountType)) {
        return accountTypeToAccountClass[accountType];
    }
    throw new Error(`Unknown account type: ${accountType}`);
};

export const isBalanceSheetAccountType = (accountType: string): boolean => {
    if (!isAccountType(accountType)) {
        throw new Error(`Unknown account type: ${accountType}`);
    }
    return (
        toBalanceSheetAccountClass(getAccountClassForType(accountType)) !== null
    );
};

export const isCorporationLedger = (entityType: EntityProfileType) =>
    [EntityProfileType.C_CORP, EntityProfileType.S_CORP].includes(entityType);

export const isPartnershipLedger = (entityType: EntityProfileType) =>
    entityType === EntityProfileType.PARTNERSHIP ||
    entityType === EntityProfileType.MMLLC;

export const isSolePropLedger = (entityType: EntityProfileType) =>
    [
        EntityProfileType.SOLE_PROPRIETORSHIP,
        EntityProfileType.NO_LEGAL_ENTITY,
        EntityProfileType.SMLLC,
    ].includes(entityType);
